import React, { useState, useEffect, useContext, useRef } from "react";
import { Search, ArrowLeft, Info } from "lucide-react";
import { useQuery } from "react-query";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { Badge } from "../../ui/badge";
import { usePhoneInventoryStore } from "../../../stores/usePhoneInventoryStore";
import GenericModal from "../../GenericModal";
import {
  searchAvailablePhoneNumbers,
  purchasePhoneNumber,
  getTelephonyUsersSimple,
} from "../../../apis/phoneInventory";
import SharedDataContext from "../../../SharedDataContext";
import { useParams, useSearchParams } from "react-router-dom";
import Image from "../../Tags/Image";
import { useCountries } from "../../../hooks/useCountries";
import CountrySelectItem from "./CountrySelectItem";
import { COUNTRY_CONFIG, getCountryFlag } from "../../../constants/countries";
import { removeNullKeys } from "../../../utils/GenericFn";
import VirtualTelephonyUserSelect from "../shared/VirtualTelephonyUserSelect";
import Flag from "../../Flag";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function PurchaseNumberPanel() {
  const {
    isPurchaseNumberPanelOpen,
    closePurchaseNumberPanel,
    currentStep,
    setCurrentStep,
    selectedNumber,
    setSelectedNumber,
    searchFilters,
    setSearchFilters,
    purchaseData,
    setPurchaseData,
    purchaseContext, // Context detection
    contextData, //  Context data
    completePurchase, //  Context-aware completion
  } = usePhoneInventoryStore();

  const userData = useContext(SharedDataContext);
  const { workspaceSlug } = useParams();
  const [searchParams] = useSearchParams();
  const workspaceId = userData?.activeWorkspace?.id;
  const captureEvent = useCaptureEvent();
  const panelOpenedRef = useRef(false);

  const [searchText, setSearchText] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState({ data: [], columnMetadata: {} });
  const [isSearching, setIsSearching] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState("Number");
  const [numberType, setNumberType] = useState("Local");
  const [addressRequirement, setAddressRequirement] = useState("any");

  const { countries, isAreaCodeSupported, getCountryByCode } = useCountries();

  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCountryDropdownOpen && !event.target.closest(".country-dropdown-container")) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryDropdownOpen]);

  useEffect(() => {
    if (isPurchaseNumberPanelOpen && workspaceId) {
      if (!panelOpenedRef.current) {
        captureEvent("NUM_PanelOpen", {
          context: purchaseContext || "inventory",
          country: searchFilters.country,
        });
        panelOpenedRef.current = true;
      }

      const country = searchParams.get("purchase_country") || "US";
      if (searchParams.has("purchase_country")) {
        setSearchFilters((prev) => ({ ...prev, country }));
      }

      handleSearch(true);
    } else {
      setAvailableNumbers({ data: [], columnMetadata: {} });
      panelOpenedRef.current = false;
    }
  }, [isPurchaseNumberPanelOpen, searchParams, workspaceId]);

  // Fetch users with dialer access from Dialer AI table API
  const {
    data: telephonyUsersResponse,
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
  } = useQuery(
    ["get_purchase_panel_users", workspaceId],
    () =>
      getTelephonyUsersSimple({
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      }),
    {
      enabled: !!workspaceId && isPurchaseNumberPanelOpen,
      retry: 2,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error(" Error fetching dialer users for purchase panel:", error);
      },
      onSuccess: (data) => {
        // Users loaded successfully
      },
    }
  );

  const availableUsers =
    telephonyUsersResponse?.data?.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email,
    })) || [];

  const contextSelectedUser = contextData?.selectedUser
    ? {
        id: contextData.selectedUser.id,
        name: contextData.selectedUser.name || contextData.selectedUser.email || "Selected User",
        email: contextData.selectedUser.email,
      }
    : null;

  const getContextConfig = () => {
    const isAddUserContext = purchaseContext === "add-user";

    return {
      title: "Purchase a new number",
      subtitle: isAddUserContext
        ? "This number will be automatically assigned to the user"
        : "Add a new phone number to your workspace",

      actionButton: {
        text: isAddUserContext ? "Choose" : "Purchase",
        className: isAddUserContext
          ? "bg-brand-600 hover:bg-brand-700 text-white border-0"
          : "border border-border text-gray-700 hover:bg-gray-50",
        variant: isAddUserContext ? "purple_outline" : "default",
      },

      confirmButton: {
        text: isAddUserContext ? "Confirm selection" : "Confirm purchase",
        className: "bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 text-sm font-semibold",
      },

      userAssignment: {
        disabled: isAddUserContext,
        users: isAddUserContext && contextSelectedUser ? [contextSelectedUser] : availableUsers,
        showContext: isAddUserContext ? "(Selected User)" : "",
      },
    };
  };

  // Render capabilities using existing Badge component
  const renderCapabilities = (capabilities) => {
    const caps = [];
    if (capabilities?.call || capabilities?.voice)
      caps.push(
        <Badge
          key="call"
          variant="outline"
          className="text-blue-600 border-blue-200 bg-blue-50 text-xs px-1.5 py-0.5 flex items-center"
        >
          <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 flex-shrink-0"></span>
          Call
        </Badge>
      );
    if (capabilities?.sms)
      caps.push(
        <Badge
          key="sms"
          variant="outline"
          className="text-blue-600 border-blue-200 bg-blue-50 text-xs px-1.5 py-0.5 flex items-center"
        >
          <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 flex-shrink-0"></span>
          SMS
        </Badge>
      );

    return caps;
  };

  // Helper function to render capabilities as text for API transformation
  const renderCapabilitiesText = (capabilities) => {
    const caps = [];
    if (capabilities?.voice) caps.push("Voice");
    if (capabilities?.sms) caps.push("SMS");
    if (capabilities?.mms) caps.push("MMS");
    if (capabilities?.fax) caps.push("Fax");
    return caps.length > 0 ? caps.join(", ") : "None";
  };

  const handleSearch = async (autoSearch = false) => {
    if (!autoSearch) {
      captureEvent("NUM_SearchClick", {
        country: searchFilters.country,
        search_criteria: searchCriteria,
        number_type: numberType,
        address_requirement: addressRequirement,
        capabilities: searchFilters.capabilities || [],
      });
    }
    setIsSearching(true);
    try {
      const searchPayload = {
        country: searchFilters.country,
        area_code: searchCriteria === "Area code" && areaCode ? areaCode : null,
        contains: searchCriteria === "Number" && searchText ? searchText : null,
        in_locality: searchCriteria === "Locality" && searchText ? searchText : null,
        in_region: searchCriteria === "Region" && searchText ? searchText : null,
        number_type: numberType.toLowerCase(),
        address_requirements: addressRequirement,
        limit: 30,
        capabilities: searchFilters.capabilities?.map((cap) =>
          cap === "Call" ? "voice" : cap === "SMS" ? "sms" : cap.toLowerCase()
        ) || ["voice", "sms"],
      };

      console.log("searchPayload", searchPayload);

      const response = await searchAvailablePhoneNumbers({
        queryParams: removeNullKeys(searchPayload),
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });

      const transformedData = {
        data: response.data
          ? response.data.map((number) => ({
              ...number,

              id: number.sid || `num_${Math.random().toString(36).substr(2, 9)}`,
              location: `${number.locality || ""}, ${number.region || ""} ${number.country || ""}`.trim(),
              number_type: number.number_type,

              capabilities_display: renderCapabilitiesText(number.capabilities),

              monthly_cost_display: `$${parseFloat(number.monthly_cost || 1.0).toFixed(2)}`,
              setup_cost_display: `$${parseFloat(number.setup_cost || 0.0).toFixed(2)}`,
            }))
          : [],
        columnMetadata: response.columnMetadata || {},
      };

      setAvailableNumbers(transformedData);

      if (!autoSearch) {
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setAvailableNumbers({ data: [], columnMetadata: {} });
    } finally {
      setIsSearching(false);
    }
  };

  const clickSearchButton = () => {
    setTimeout(() => {
      const ph__search__button = document.getElementById("ph__search__button");
      if (ph__search__button) {
        ph__search__button.click();
      }
    }, 500);
  };

  const handleResetFilters = () => {
    captureEvent("NUM_ResetFiltersClick");
    setSearchText("");
    setAreaCode("");
    setSearchCriteria("Number");
    setNumberType("Local");
    setAddressRequirement("any");
    setSearchFilters({
      country: "US",
      capabilities: ["Call", "SMS"],
    });
    setAvailableNumbers({ data: [], columnMetadata: {} });
    clickSearchButton();
  };

  const handleSelectNumber = (number) => {
    captureEvent("NUM_NumberSelectClick", {
      phone_number: number.phone_number,
      number_type: number.number_type,
      monthly_cost: number.monthly_cost,
      context: purchaseContext || "inventory",
    });
    setSelectedNumber(number);

    // Context-aware behavior
    if (purchaseContext === "add-user") {
      // For Add User context: immediately close panel and send number to form
      completePurchase(number);
      closePurchaseNumberPanel();
    } else {
      // For Inventory context: continue to configuration step
      setCurrentStep(3);
    }
  };

  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const handleConfirmPurchase = async () => {
    captureEvent("NUM_PurchaseConfirmClick", {
      phone_number: selectedNumber.phone_number,
      access_type: purchaseData.numberAccess,
      assigned_users_count: purchaseData.assignedUsers?.length || 0,
      context: purchaseContext || "inventory",
    });
    setPurchaseLoading(true);
    try {
      const purchaseRequestData = {
        phone_number: selectedNumber.phone_number,
        label: purchaseData.numberLabel,
        type: purchaseData.numberAccess === "assigned" ? "Assigned" : "Shared",
        assign_to_users: purchaseData.numberAccess === "assigned" ? purchaseData.assignedUsers : undefined,
      };

      // purchaseRequestData.phone_number = "+18158843168";
      // purchaseRequestData.label = "+18158843168";

      const response = await purchasePhoneNumber({
        data: purchaseRequestData,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });

      setPurchaseLoading(false);

      captureEvent("NUM_PurchaseSuccess", {
        phone_number: response.data?.phone_number || selectedNumber.phone_number,
        number_id: response.data?.id,
        context: purchaseContext || "inventory",
      });

      // Context-aware completion
      completePurchase(response.data || selectedNumber);
      closePurchaseNumberPanel();
    } catch (error) {
      console.error("Purchase failed:", error);
      setPurchaseLoading(false);
      captureEvent("NUM_PurchaseFailure", {
        phone_number: selectedNumber.phone_number,
        error: error?.message || "unknown_error",
        context: purchaseContext || "inventory",
      });
    }
  };

  const handleCapabilityChange = (capability, checked) => {
    captureEvent("NUM_CapabilityChange", {
      capability,
      checked,
    });
    const currentCapabilities = searchFilters.capabilities || [];
    if (checked) {
      setSearchFilters({
        ...searchFilters,
        capabilities: [...currentCapabilities, capability],
      });
    } else {
      setSearchFilters({
        ...searchFilters,
        capabilities: currentCapabilities.filter((c) => c !== capability),
      });
    }
  };

  // Check if area code input should be disabled
  const isAreaCodeDisabled = () => {
    return !isAreaCodeSupported(searchFilters.country);
  };

  useEffect(() => {
    if (!workspaceId) {
      return;
    }
    if (searchCriteria !== "Area code") return;
    if (isAreaCodeDisabled()) return;

    const shouldTriggerSearch = areaCode.length === 3 || areaCode.length === 0;
    if (!shouldTriggerSearch) return;

    const debounceId = setTimeout(() => {
      handleSearch(true);
    }, 500);

    return () => clearTimeout(debounceId);
  }, [areaCode, searchCriteria, searchFilters?.country, workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      return;
    }
    if (searchCriteria === "Area code") return;

    const debounceId = setTimeout(() => {
      handleSearch(true);
    }, 500);

    return () => clearTimeout(debounceId);
  }, [searchCriteria, searchText, workspaceId]);

  const returnPlaceHolder = (searchCriteria) => {
    if (searchCriteria === "Number") {
      return "Search by digits";
    } else if (searchCriteria === "Locality") {
      return "Search by locality";
    } else if (searchCriteria === "Region") {
      return "Search by region";
    } else {
      return "Search...";
    }
  };

  const renderStep1 = () => {
    const selectedCountry = searchFilters.country ? getCountryByCode(searchFilters.country) : null;

    return (
      <div className="space-y-4">
        {/* Country and Capabilities Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Country</label>
            <div className="relative country-dropdown-container">
              <Input
                placeholder="Search for a country..."
                value={
                  selectedCountry
                    ? `(+${selectedCountry?.phoneCode}) ${selectedCountry?.name} - ${selectedCountry?.code}`
                    : ""
                }
                onClick={() => setIsCountryDropdownOpen(true)}
                readOnly
                className="cursor-pointer bg-background border border-blue-300 focus:border-blue-500 rounded-lg pl-11 pr-10"
              />
              {selectedCountry && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 border border-border rounded-full overflow-hidden inline-flex leading-none">
                  <Flag code={selectedCountry.code} squared />
                </span>
              )}
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 bg-background" />

              {/* Country Dropdown */}
              {isCountryDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-gray-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                  {/* Search Input */}
                  <div className="sticky top-0 bg-background border-b border-gray-200 p-3 z-10">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 bg-background" />
                      <Input
                        placeholder="Search countries..."
                        value={countrySearchTerm}
                        className="pl-10 pr-3 py-2 text-sm border-0 focus:ring-0 focus:border-0"
                        onChange={(e) => setCountrySearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Country List */}
                  {countries
                    .filter(
                      (country) =>
                        country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
                        country.code.toLowerCase().includes(countrySearchTerm.toLowerCase())
                    )
                    .map((country) => (
                      <div
                        key={country.code}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                          searchFilters.country === country.code ? "bg-blue-100" : ""
                        }`}
                        onClick={() => {
                          captureEvent("NUM_CountryChange", {
                            from_country: searchFilters.country,
                            to_country: country.code,
                          });
                          setSearchFilters({ ...searchFilters, country: country.code });
                          setIsCountryDropdownOpen(false);
                          setCountrySearchTerm(""); // Clear search when selecting
                          clickSearchButton();
                        }}
                      >
                        <CountrySelectItem
                          flag={
                            <span className="border border-border rounded-full overflow-hidden inline-flex leading-none">
                              <Flag code={country.code} squared />
                            </span>
                          }
                          name={country.name}
                          phoneCode={country.phoneCode}
                          code={country.code}
                        />
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Capabilities</label>
            <div className="flex items-center space-x-4">
              {["Call", "SMS"].map((capability) => (
                <div key={capability} className="flex items-center space-x-2">
                  <Checkbox
                    id={capability}
                    checked={searchFilters.capabilities?.includes(capability) || false}
                    onCheckedChange={(checked) => handleCapabilityChange(capability, checked)}
                    className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600"
                  />
                  <label htmlFor={capability} className="text-sm text-gray-600 font-medium">
                    {capability}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Search Input, Number Type, and Address Requirements Row */}
        <div className="grid grid-cols-3 gap-4">
          {/* Search Input Column */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Search criteria</label>
            {searchCriteria === "Area code" ? (
              <div>
                <Input
                  placeholder="Enter area code (e.g., 415)"
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                  disabled={isAreaCodeDisabled()}
                  className={isAreaCodeDisabled() ? "bg-gray-100 cursor-not-allowed" : ""}
                  maxLength={3}
                />
                {isAreaCodeDisabled() && (
                  <p className="text-xs text-red-500 mt-1">
                    Area code search only works for{" "}
                    {COUNTRY_CONFIG.AREA_CODE_SUPPORT.map((code) => getCountryByCode(code)?.name).join(
                      " and "
                    )}
                  </p>
                )}
              </div>
            ) : (
              <Input
                placeholder={returnPlaceHolder(searchCriteria)}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            )}
          </div>

          {/* Number Type Column */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Number type
              <Info className="inline h-3 w-3 ml-1 text-gray-400" />
            </label>
            <Select value={numberType} onValueChange={setNumberType}>
              <SelectTrigger>
                <SelectValue placeholder="Any (Local/Mobile/Toll-free)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Local">Local</SelectItem>
                <SelectItem value="Mobile">Mobile</SelectItem>
                <SelectItem value="Toll-free">Toll-free</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Address Requirements Column */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Address requirements
              <Info className="inline h-3 w-3 ml-1 text-gray-400" />
            </label>
            <Select value={addressRequirement} onValueChange={setAddressRequirement}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="local">Exclude local requirements</SelectItem>
                <SelectItem value="foreign">Exclude foreign requirements</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Radio Buttons Row */}
        <div className="flex items-center space-x-6">
          {["Number", "Locality", "Area code", "Region"].map((criteria) => (
            <div key={criteria} className="flex items-center space-x-2">
              <input
                type="radio"
                id={criteria}
                name="searchCriteria"
                value={criteria}
                checked={searchCriteria === criteria}
                onChange={(e) => setSearchCriteria(e.target.value)}
                className="w-4 h-4 text-[#7F56D9] border-gray-300 focus:ring-[#7F56D9]"
              />
              <label htmlFor={criteria} className="text-sm text-gray-600 font-medium">
                {criteria}
              </label>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-2">
          <Button
            id="ph__search__button"
            onClick={() => {
              handleSearch();
            }}
            disabled={isSearching}
            className="bg-[#7F56D9] text-white px-4 py-2"
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>

          <Button variant="outline" onClick={handleResetFilters} className=" text-[#535862] px-4 py-2">
            Reset filters
          </Button>
        </div>
      </div>
    );
  };

  const getSortedVisibleColumns = (columnMetadata) => {
    if (!columnMetadata || Object.keys(columnMetadata).length === 0) {
      return [
        { key: "phone_number", display_name: "Number", order: 0 },
        { key: "number_type", display_name: "Type", order: 1 },
        { key: "capabilities", display_name: "Channels", order: 2 },
        { key: "address_requirement", display_name: "Address Requirement", order: 3 },
        { key: "monthly_cost", display_name: "Monthly Fees", order: 4 },
      ];
    }

    return Object.keys(columnMetadata)
      .map((key) => ({
        key,
        ...columnMetadata[key],
      }))
      .filter((col) => col.visible)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Cell content renderer similar to other tables
  const renderCellContent = (columnKey, item, config) => {
    switch (columnKey) {
      case "phone_number":
        return (
          <div className="flex items-center">
            <span className="text-[36px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border  mr-2">
              <Flag code={item?.country} squared />
            </span>
            <div className="">
              <div>
                <div className="font-semibold text-gray-950 text-sm capitalize">{item.phone_number}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-normal mt-0.5">
                  {item.location}
                </div>
              </div>
            </div>
          </div>
        );

      case "number_type":
        return (
          <>
            {item.number_type ? (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border">
                {item.number_type}
              </span>
            ) : (
              "-"
            )}
          </>
        );

      case "capabilities":
        return <div className="flex items-center gap-1">{renderCapabilities(item.capabilities)}</div>;

      case "address_requirement":
        return <span className="text-gray-600 text-sm">{item.address_requirement || "None"}</span>;

      case "monthly_cost":
        return (
          <span className="font-bold text-gray-950">{item.monthly_cost_display || item.monthly_cost}</span>
        );

      default:
        return <span className="text-gray-600 text-sm">{item[columnKey] || "-"}</span>;
    }
  };

  // Enhanced results table with columnMetadata support
  const renderStep2 = () => {
    const config = getContextConfig();
    const columns = getSortedVisibleColumns(availableNumbers.columnMetadata);

    return (
      <div className="space-y-4">
        {/* Dynamic Table Header based on columnMetadata */}
        <div className="overflow-hidden border border-border rounded-[--radius]">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className="px-3 py-3.5 text-left text-xs font-medium text-text-600"
                  >
                    <div className="flex items-center">
                      {column.display_name}
                      {column.key === "monthly_cost" && (
                        <Info className="inline h-3 w-3 ml-1 text-[#9CA3AF]" />
                      )}
                    </div>
                  </th>
                ))}
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-text-600">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-background">
              {availableNumbers.data && availableNumbers.data.length > 0 ? (
                availableNumbers.data.map((number) => (
                  <tr key={number.id} className="hover:bg-gray-50">
                    {columns.map((column) => (
                      <td key={column.key} className="px-3 py-4 whitespace-nowrap text-sm">
                        {renderCellContent(column.key, number, config)}
                      </td>
                    ))}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <Button
                        variant={config.actionButton.variant}
                        size="sm"
                        onClick={() => handleSelectNumber(number)}
                        // className={`text-xs px-4 py-2 min-w-[80px] ${config.actionButton.className}`}
                      >
                        {config.actionButton.text}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : !isSearching ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-[#535862]">
                    No numbers found. Try adjusting your search criteria.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {isSearching && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#6366F1]"></div>
            <p className="mt-2 text-[#535862]">Searching for available numbers...</p>
          </div>
        )}
      </div>
    );
  };

  // Context-aware user assignment section
  const renderUserAssignmentSection = () => {
    const config = getContextConfig();
    const baseUsers = config.userAssignment.users || [];
    const baseUserIds = new Set(baseUsers.filter((user) => user?.id).map((user) => user.id));

    const userMap = new Map();

    [...availableUsers, ...baseUsers].forEach((user) => {
      if (!user || !user.id) return;
      const shouldAnnotate = baseUserIds.has(user.id) && config.userAssignment.showContext;
      userMap.set(user.id, {
        ...user,
        note: shouldAnnotate ? config.userAssignment.showContext : user.note,
      });
    });

    purchaseData.assignedUsers.forEach((userId) => {
      if (!userMap.has(userId)) {
        userMap.set(userId, { id: userId, name: userId });
      }
    });

    const usersToDisplay = Array.from(userMap.values());

    return (
      <div className="mt-3">
        <VirtualTelephonyUserSelect
          selectedIds={purchaseData.assignedUsers}
          onChange={(ids) => setPurchaseData({ assignedUsers: ids })}
          disabled={config.userAssignment.disabled}
          placeholder="Select users"
          emptyMessage="No users with dialer access found"
          errorMessage="Error loading users"
          workspaceId={workspaceId}
          preloadedUsers={usersToDisplay}
        />
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6 p-6 bg-gray-50">
      {/* Number Preview Card */}
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-950">Number Preview</h3>
        </div>

        {/* Selected Number Display */}
        {selectedNumber && (
          <div className="px-4 mb-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full flex items-start justify-center leading-none">
                  <span className="text-[40px] rounded-full overflow-hidden border border-border">
                    <Flag code={selectedNumber?.country} squared />
                  </span>
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-950">{selectedNumber.phone_number}</div>
                <div className="flex items-center space-x-2 mt-2">
                  <div className="flex space-x-1">{renderCapabilities(selectedNumber.capabilities)}</div>
                  <span className="text-sm text-gray-500">{selectedNumber.location}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cost and Registration Info */}
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <div className=" w-6 h-6 rounded flex items-center justify-center mt-0.5">
              <Image
                src="/images/icons/untitledui/number-inventory/dollar-phonepanel.svg"
                alt="Dollar icon"
                className="w-5 h-5"
              />
            </div>
            <span className="text-sm text-gray-600">
              Adding this number will debit <strong className="text-gray-600">$6/month</strong> from your
              {process.env.REACT_APP_APP_NAME} credits
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-6 h-6  rounded-full flex items-center justify-center mt-0.5">
              <Image
                src="/images/icons/untitledui/number-inventory/massage-phonepanel.svg"
                alt="Dollar icon"
                className="w-5 h-5"
              />
            </div>
            <span className="text-sm text-gray-600">
              To use SMS services, You must{" "}
              <button className="text-[#6366F1] hover:underline font-medium">
                register this number in 10DLC
              </button>
            </span>
          </div>
        </div>
      </div>

      {/* Number Access Card */}
      <div className="bg-background border border-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-950 mb-2">Number Access</h3>
          <p className="text-sm text-gray-600">
            Assign number to a single user, set of users or accessible across the entire workspace for all
            users with dialer access.
          </p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Assign Users Option */}
          <div
            onClick={() => {
              setPurchaseData({ numberAccess: "assigned" });
            }}
            className={`border rounded-lg p-4 ${purchaseData.numberAccess === "assigned" ? "border-2 border-[#9E77ED] bg-background]" : "border-border bg-background"}`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="assign-users-preview"
                name="access-type-preview"
                value="assigned"
                checked={purchaseData.numberAccess === "assigned"}
                onChange={(e) => setPurchaseData({ numberAccess: e.target.value })}
                className="mt-0.5 h-4 w-4 text-[#8B5CF6] border-border focus:ring-[#8B5CF6]"
              />
              <div className="flex-1">
                <label htmlFor="assign-users-preview" className="text-sm font-semibold text-gray-950 block">
                  Assign users
                </label>
                <p className="text-sm text-gray-600 mt-1">Number is accessible to all the selected users</p>

                {purchaseData.numberAccess === "assigned" && renderUserAssignmentSection()}
              </div>
            </div>
          </div>

          {/* Workspace Access Option */}
          <div
            onClick={() => {
              setPurchaseData({ numberAccess: "workspace" });
            }}
            className={`border rounded-lg p-4 ${purchaseData.numberAccess === "workspace" ? "border-2 border-[#9E77ED] bg-background" : "border-border bg-background"}`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="workspace-access-preview"
                name="access-type-preview"
                value="workspace"
                checked={purchaseData.numberAccess === "workspace"}
                onChange={(e) => setPurchaseData({ numberAccess: e.target.value })}
                className="mt-0.5 h-4 w-4 text-[#8B5CF6] border-border focus:ring-[#8B5CF6]"
              />
              <div className="flex-1">
                <label
                  htmlFor="workspace-access-preview"
                  className="text-sm font-semibold text-gray-950 block"
                >
                  Accessible across workspace
                </label>
                <p className="text-sm text-gray-600 mt-1">
                  This number will be available to everyone in the workspace with dialer access
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Number Label Section - Inside Number Access Card */}
        <div className="border-t border-border pt-6">
          <label className="block text-sm font-semibold text-gray-950 mb-3">
            Number Label
            <Info className="inline h-4 w-4 ml-1 text-gray-500" />
          </label>
          <Input
            placeholder="Add a number label"
            value={purchaseData.numberLabel}
            onChange={(e) => setPurchaseData({ numberLabel: e.target.value })}
            className="border-border"
          />
        </div>
      </div>
    </div>
  );

  const renderSearchAndResults = () => (
    <div className="space-y-0">
      {/* Search Form */}
      <div className="rounded-lg bg-gray-50 p-6">{renderStep1()}</div>

      {/* Results Table */}
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-950">
            Available Numbers{" "}
            {availableNumbers.data && availableNumbers.data.length > 0 && `(${availableNumbers.data.length})`}
          </h3>
        </div>
        {renderStep2()}
      </div>
    </div>
  );

  const config = getContextConfig();

  return (
    <GenericModal
      isOpen={isPurchaseNumberPanelOpen}
      closeModal={closePurchaseNumberPanel}
      title=""
      drawer={true}
      dismiss={false}
      drawerWidth="max-w-[900px] min-w-[900px]  bg-gray-50"
    >
      <div className="h-full flex flex-col">
        {/* Context-aware header */}
        <div className="pb-4 border-b border-gray-200 px-6 pt-6">
          {/* Close Button */}
          <div className="mb-4">
            <button
              onClick={() => {
                captureEvent("NUM_PanelClose");
                closePurchaseNumberPanel();
              }}
              className="flex items-center text-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-sm font-semibold text-gray-600">Close</span>
            </button>
          </div>

          {/* Title, Description and Button Row */}
          <div className="flex items-start justify-between">
            {/* Title and Description */}
            <div className="flex-1">
              <p className="text-lg text-gray-950 font-semibold">{config.title}</p>
              <p className="text-sm  text-gray-700 font-normal mt-1">{config.subtitle}</p>
            </div>

            {/* Show context-aware confirm button only on step 3 */}
            {currentStep === 3 && (
              <div className="flex-shrink-0 ml-6">
                <Button onClick={handleConfirmPurchase} className={config.confirmButton.className}>
                  {config.confirmButton.text} {purchaseLoading && <div className="loader_circle ml-2"></div>}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-6">
          {currentStep === 3 ? renderStep3() : renderSearchAndResults()}
        </div>
      </div>
    </GenericModal>
  );
}